import { useEffect, useState } from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { BookImage, ChevronDown, GalleryHorizontal, GripVertical, Heading, Image, Images, LayoutGrid, Megaphone, Minus, Plus, RotateCcw, Trash2, Type, Video } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { SortableArea } from "@/components/admin/Sortable";
import { UploadBtn } from "@/components/admin/SermonForm";
import { fileUrl } from "@/lib/api";
import { BLOCK_LABEL, BLOCK_TYPES, PAGE_SECTIONS, addBlock, blockTestid, kindOf, layoutOf, pageKeyOf } from "@/lib/blocks";
import { homeOrder } from "@/lib/design";
import { embedUrl } from "@/lib/media";
import { TextStyle } from "@/components/admin/ve/TextStyle";
import { DividerFields, SlideshowFields } from "@/components/admin/ve/BlockExtras";
import { VideosFields } from "@/components/admin/ve/VideosFields";
import { PhotosFields } from "@/components/admin/ve/PhotosFields";
import { AlbumsFields } from "@/components/admin/ve/AlbumsFields";
import { toast } from "sonner";

const ICON = { heading: Heading, text: Type, announcement: Megaphone, photo: Image, slideshow: GalleryHorizontal, video: Video, videos: LayoutGrid, photos: Images, albums: BookImage, divider: Minus };
const input = "w-full rounded-lg border border-[#14213D]/15 bg-white px-2.5 py-2 font-ui text-xs text-[#14213D] outline-none focus:border-[#C8862F]";

// Text box that saves when you click away, so typing doesn't send a change on every key.
const Field = ({ label, value = "", onSave, rows = 0, testid }) => {
  const [v, setV] = useState(value);
  useEffect(() => { setV(value); }, [value]);
  const Tag = rows ? "textarea" : "input";
  return (
    <label className="block">
      <span className="mb-1 block font-ui text-[10px] uppercase tracking-widest text-[#5F6875]">{label}</span>
      <Tag data-testid={testid} rows={rows || undefined} value={v} onChange={(e) => setV(e.target.value)} onBlur={() => v !== value && onSave(v)} className={input} />
    </label>
  );
};

const BlockFields = ({ b, onChange }) => {
  const set = (k) => (v) => onChange({ ...b, [k]: v });
  const t = `ve-block-${b.id}`;
  const hasTitle = b.type === "announcement";
  const media = b.type === "photo" || b.type === "video" || b.type === "slideshow";
  if (b.type === "divider") return <div className="border-t border-[#14213D]/10 p-3"><DividerFields b={b} onChange={onChange} /></div>;
  if (b.type === "videos") return <div className="border-t border-[#14213D]/10 p-3"><VideosFields b={b} onChange={onChange} /></div>;
  if (b.type === "photos") return <div className="border-t border-[#14213D]/10 p-3"><PhotosFields b={b} onChange={onChange} /></div>;
  if (b.type === "albums") return <div className="border-t border-[#14213D]/10 p-3"><AlbumsFields b={b} onChange={onChange} /></div>;
  return (
    <div className="grid gap-2 border-t border-[#14213D]/10 p-3">
      {b.type === "slideshow" && <SlideshowFields b={b} onChange={onChange} />}
      {media && b.type !== "slideshow" && (
        <div className="flex flex-wrap items-center gap-2">
          {b.url && b.type === "photo" && <img src={fileUrl(b.url)} alt="" className="h-12 w-16 rounded-lg object-cover" />}
          <UploadBtn accept={b.type === "video" ? "video/*" : "image/*"} testid={`${t}-upload`} label={b.url ? `Replace ${b.type}` : `Upload ${b.type}`} onDone={(u) => set("url")(u)} />
        </div>
      )}
      {b.type === "video" && <Field label="…or paste a YouTube / Facebook link" value={b.url} onSave={set("url")} testid={`${t}-url`} />}
      {hasTitle && <Field label="Title (English)" value={b.title_en} onSave={set("title_en")} testid={`${t}-title-en`} />}
      {hasTitle && <Field label="Title (Marshallese)" value={b.title_mh} onSave={set("title_mh")} testid={`${t}-title-mh`} />}
      <Field label={media ? "Caption (English)" : "English"} rows={media || b.type === "heading" ? 0 : 4} value={b.en} onSave={set("en")} testid={`${t}-en`} />
      <Field label={media ? "Caption (Marshallese)" : "Marshallese"} rows={media || b.type === "heading" ? 0 : 4} value={b.mh} onSave={set("mh")} testid={`${t}-mh`} />
      {(b.type === "text" || b.type === "heading") && <TextStyle b={b} onChange={onChange} />}
    </div>
  );
};

const summary = (b) => {
  if (b.type === "divider") return `${b.line || "solid"} line`;
  if (b.type === "slideshow") return `${(b.urls || []).length} photo(s)`;
  if (b.type === "videos") return `${(b.items || []).length} video(s)`;
  if (b.type === "photos") return `${(b.items || []).length} photo(s)`;
  if (b.type === "albums") return `${(b.albums || []).length} album(s)`;
  if (b.type === "photo" || b.type === "video") return b.en || (b.url ? "" : "(no file yet)");
  return b.title_en || b.en || "(empty)";
};

const DRAG_TYPE = "application/x-ve-block";
const dragsBlock = (e) => Array.from(e.dataTransfer?.types || []).includes(DRAG_TYPE);

// Lets a block from the "Add to this page" tray be dropped right above a row.
const useDrop = (onDrop) => {
  const [over, setOver] = useState(false);
  return {
    over,
    props: {
      onDragOver: (e) => { if (dragsBlock(e)) { e.preventDefault(); setOver(true); } },
      onDragLeave: () => setOver(false),
      onDrop: (e) => { if (!dragsBlock(e)) return; e.preventDefault(); setOver(false); onDrop(e.dataTransfer.getData(DRAG_TYPE)); },
    },
  };
};

const Row = ({ b, labels, open, onOpen, onChange, onDelete, isOn, onDropBlock }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: b.id });
  const drop = useDrop(onDropBlock);
  const builtin = b.type === "section";
  const Icon = ICON[b.type];
  return (
    <li ref={setNodeRef} {...drop.props} style={{ transform: CSS.Transform.toString(transform), transition }} data-testid={`ve-layout-row-${b.id}`} className={`rounded-xl border bg-white ${drop.over ? "border-t-4 border-t-[#2563EB]" : ""} ${isDragging ? "z-10 border-[#C8862F] shadow-lg" : builtin ? "border-[#14213D]/10" : "border-[#2563EB]/25"}`}>
      <div className="flex items-center gap-2 px-2 py-2">
        <button type="button" data-testid={`ve-layout-drag-${b.id}`} {...attributes} {...listeners} aria-label="Drag to reorder" className="cursor-grab touch-none rounded-lg p-1 text-[#5F6875] active:cursor-grabbing"><GripVertical className="h-4 w-4" /></button>
        {builtin ? (
          <>
            <span className={`flex-1 font-ui text-sm ${!isOn(b) ? "text-[#5F6875] line-through" : "text-[#14213D]"}`}>{labels[b.key] || b.key}</span>
            <Switch data-testid={`ve-layout-toggle-${b.key}`} checked={isOn(b)} onCheckedChange={(v) => onChange({ ...b, hidden: !v })} />
          </>
        ) : (
          <>
            <button type="button" data-testid={`ve-layout-open-${b.id}`} onClick={onOpen} className="flex min-w-0 flex-1 items-center gap-2 text-left">
              <Icon className="h-4 w-4 shrink-0 text-[#2563EB]" />
              <span className={`min-w-0 flex-1 truncate font-ui text-sm ${isOn(b) ? "text-[#14213D]" : "text-[#5F6875] line-through"}`}><b>{BLOCK_LABEL[b.type]}</b> <span className="text-[#5F6875]">{isOn(b) ? summary(b) : "(hidden)"}</span></span>
              <ChevronDown className={`h-4 w-4 shrink-0 text-[#5F6875] transition-transform ${open ? "rotate-180" : ""}`} />
            </button>
            <Switch data-testid={`ve-layout-toggle-${b.id}`} checked={isOn(b)} onCheckedChange={(v) => onChange({ ...b, hidden: !v })} aria-label="Show on the website" />
            <button type="button" data-testid={`ve-layout-delete-${b.id}`} onClick={onDelete} aria-label="Delete block" className="rounded-full p-1.5 text-[#B4442A] hover:bg-[#B4442A]/10"><Trash2 className="h-3.5 w-3.5" /></button>
          </>
        )}
      </div>
      {open && !builtin && <BlockFields b={b} onChange={onChange} />}
    </li>
  );
};

// Paste a YouTube or Facebook link and it becomes a video block on this page.
const QuickVideo = ({ onAdd }) => {
  const [url, setUrl] = useState("");
  const add = () => {
    const u = url.trim();
    if (!embedUrl(u)) return toast.error("Please paste a YouTube or Facebook video link.");
    onAdd(u);
    setUrl("");
    return toast.success("Video added. Drag it in the list to move it.");
  };
  return (
    <div data-testid="ve-quick-video" className="rounded-xl border border-[#2563EB]/30 bg-white p-3">
      <p className="flex items-center gap-1.5 font-ui text-xs font-semibold text-[#14213D]"><Video className="h-4 w-4 text-[#2563EB]" /> Add a YouTube or Facebook video</p>
      <div className="mt-2 flex gap-2">
        <input data-testid="ve-quick-video-input" value={url} onChange={(e) => setUrl(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} placeholder="Paste the video link here" className={input} />
        <button type="button" data-testid="ve-quick-video-add" onClick={add} className="shrink-0 rounded-lg px-3 font-ui text-xs font-semibold" style={{ backgroundColor: "#2563EB", color: "#FFFFFF" }}>Add</button>
      </div>
    </div>
  );
};

// Scrolls the preview to a block and flashes it, so a new block is easy to find even at the bottom of a long page.
const showInPreview = (id, tries = 12) => {
  const doc = document.querySelector("iframe")?.contentDocument;
  const el = doc?.querySelector(`[data-testid="block-${id}"]`);
  if (!el) {
    if (tries > 0) setTimeout(() => showInPreview(id, tries - 1), 400);
    return;
  }
  el.scrollIntoView({ behavior: "smooth", block: "center" });
  el.animate([{ outline: "4px solid #2563EB", outlineOffset: "6px" }, { outline: "4px solid transparent", outlineOffset: "6px" }], { duration: 1800 });
};

// "This page" tab: reorder, show/hide and add content blocks on whatever page the preview is showing.
const EndDrop = ({ onDrop }) => {
  const drop = useDrop(onDrop);
  return <div {...drop.props} data-testid="ve-drop-end" className={`rounded-xl border-2 border-dashed px-3 py-3 text-center font-ui text-[11px] transition-colors ${drop.over ? "border-[#2563EB] bg-[#2563EB]/10 text-[#2563EB]" : "border-[#14213D]/15 text-[#5F6875]"}`}>Drop a block here to add it at the bottom of the page</div>;
};

export const PagePanel = ({ path, data = {}, onPatch, pageName }) => {
  const key = pageKeyOf(path);
  const [open, setOpen] = useState("");
  if (!key) return <p className="text-xs text-[#4A5568]">Pick a page at the top to edit it.</p>;
  const home = key === "home";
  const layouts = data.layouts || {};
  const list = layoutOf(key, layouts[key], home ? homeOrder(data.home_order) : undefined);
  const labels = Object.fromEntries(PAGE_SECTIONS[kindOf(key)] || []);
  // Home sections keep using their own on/off switches and saved order, so the Sections tab stays in step.
  const body = (l) => (home ? { layouts: { home: l }, home_order: l.filter((b) => b.type === "section").map((b) => b.key) } : { layouts: { [key]: l } });
  const save = (l) => onPatch(body(l));
  // Things removed with "Select & remove" are stored separately; the switches here show and undo that too.
  const removedKey = (b) => `b:${blockTestid(key, b)}`;
  const removed = (b) => Boolean(data.hidden?.[removedKey(b)]);
  const isOn = (b) => !removed(b) && (home && b.type === "section" ? data.sections?.[b.key] !== false : !b.hidden);
  const change = (b, nb) => {
    const unremove = !nb.hidden && removed(b) ? { hidden: { [removedKey(b)]: "" } } : {};
    if (home && b.type === "section") return onPatch({ sections: { [b.key]: !nb.hidden }, ...unremove });
    return onPatch({ ...body(list.map((x) => (x.id === b.id ? nb : x))), ...unremove });
  };
  const add = (type, extra = {}) => {
    const r = addBlock(key, list, type);
    save(r.list.map((x) => (x.id === r.block.id ? { ...x, ...extra } : x)));
    setOpen(r.block.id);
    showInPreview(r.block.id);
  };
  const insertAt = (type, index) => {
    if (!BLOCK_LABEL[type]) return;
    const { block } = addBlock(key, [], type);
    save([...list.slice(0, index), block, ...list.slice(index)]);
    setOpen(block.id);
    showInPreview(block.id);
  };
  return (
    <div className="grid gap-4" data-testid="ve-page-panel">
      <div>
        <p className="font-ui text-[11px] uppercase tracking-widest text-[#8A6A12]">Editing: {pageName}</p>
        <p className="mt-1 text-xs text-[#4A5568]">Drag a block from the tray below into the list, or click it to add it. Drag rows to change the order. Click a blue block to edit it and style its text.</p>
      </div>
      <div>
        <p className="mb-2 font-ui text-[11px] uppercase tracking-widest text-[#8A6A12]">Add to this page</p>
        <div className="grid grid-cols-2 gap-2">
          {BLOCK_TYPES.map(([type, label]) => {
            const Icon = ICON[type];
            return <button type="button" key={type} draggable onDragStart={(e) => { e.dataTransfer.setData(DRAG_TYPE, type); e.dataTransfer.effectAllowed = "copy"; }} data-testid={`ve-add-block-${type}`} onClick={() => add(type)} className="flex cursor-grab items-center gap-2 rounded-xl border border-dashed border-[#2563EB]/40 bg-white px-3 py-2.5 font-ui text-xs font-semibold text-[#2563EB] hover:bg-[#2563EB]/5 active:cursor-grabbing"><Plus className="h-3.5 w-3.5" /><Icon className="h-3.5 w-3.5" /> {label}</button>;
          })}
        </div>
      </div>
      <QuickVideo onAdd={(url) => add("video", { url })} />
      <SortableArea ids={list.map((b) => b.id)} onReorder={(ids) => save(ids.map((id) => list.find((b) => b.id === id)))}>
        <ul className="grid gap-2">
          {list.map((b, i) => (
            <Row key={b.id} b={b} labels={labels} isOn={isOn} open={open === b.id} onOpen={() => { if (open !== b.id) showInPreview(b.id, 2); setOpen(open === b.id ? "" : b.id); }}
              onDropBlock={(type) => insertAt(type, i)}
              onChange={(nb) => change(b, nb)}
              onDelete={() => window.confirm(`Delete this ${BLOCK_LABEL[b.type].toLowerCase().replace(/ block$/, "")} block?`) && save(list.filter((x) => x.id !== b.id))} />
          ))}
        </ul>
      </SortableArea>
      <EndDrop onDrop={(type) => insertAt(type, list.length)} />
      {Array.isArray(layouts[key]) && (
        <button type="button" data-testid="ve-layout-reset" onClick={() => window.confirm("Remove all added blocks and put this page back to how it was?") && (home ? onPatch({ layouts: { home: "" } }) : save(""))} className="flex items-center justify-center gap-1.5 rounded-full border border-[#14213D]/15 py-2 font-ui text-xs text-[#5F6875] hover:bg-[#14213D]/5"><RotateCcw className="h-3.5 w-3.5" /> Reset this page</button>
      )}
    </div>
  );
};
